/**
 * Подписи состояний файлов словами.
 *
 * Состояние всегда называется текстом: цвет и значок только помогают, но
 * ничего не сообщают тем, кто цвета не различает или читает страницу голосом.
 */

export type ScanTone = "ok" | "wait" | "blocked";

export interface ScanStatusView {
  readonly label: string;
  readonly tone: ScanTone;
  /** Можно ли выдавать содержимое. Вне `clean` — нельзя. */
  readonly releasable: boolean;
  /** Что сказать человеку, когда скачивание недоступно. */
  readonly blockedReason: string | null;
}

/** Состояние антивирусной проверки: `clean`, `pending`, `quarantined`. */
export function scanStatusView(status: string): ScanStatusView {
  switch (status) {
    case "clean":
      return {
        label: "Проверен: угроз не найдено",
        tone: "ok",
        releasable: true,
        blockedReason: null,
      };
    case "pending":
      return {
        label: "Проверка не завершена",
        tone: "wait",
        releasable: false,
        blockedReason:
          "Файл ещё проверяется: скачать и открыть его можно после проверки.",
      };
    case "quarantined":
      return {
        label: "В карантине",
        tone: "blocked",
        releasable: false,
        blockedReason: "Файл в карантине: скачивание запрещено.",
      };
    default:
      // Незнакомое состояние не считается чистым: выдача только при `clean`.
      return {
        label: `Состояние проверки: ${status}`,
        tone: "blocked",
        releasable: false,
        blockedReason:
          "Файл не прошёл проверку, поэтому скачивание недоступно.",
      };
  }
}

export type FileSourceKind =
  | "manual"
  | "email_attachment"
  | "comment_attachment"
  | "material"
  | "other";

export interface FileSourceView {
  readonly kind: FileSourceKind;
  readonly label: string;
}

/** Происхождение файла: контракт provenance ES-35. */
export function fileSourceView(type: string): FileSourceView {
  switch (type) {
    case "manual":
      return { kind: "manual", label: "Ручная загрузка" };
    case "email_attachment":
      return { kind: "email_attachment", label: "Вложение письма" };
    case "comment_attachment":
      return { kind: "comment_attachment", label: "Вложение обсуждения" };
    case "material":
      return { kind: "material", label: "Дополнительный материал" };
    default:
      return { kind: "other", label: `Источник: ${type}` };
  }
}

export type VerificationTone = "ok" | "wait" | "blocked";

export interface VerificationView {
  readonly label: string;
  readonly tone: VerificationTone;
}

/** Результат серверной проверки подписи. */
export function verificationView(state: string): VerificationView {
  switch (state) {
    case "verified":
      return { label: "Подпись верна", tone: "ok" };
    case "invalid":
      return { label: "Подпись неверна", tone: "blocked" };
    case "pending":
      return { label: "Проверка не выполнена", tone: "wait" };
    default:
      return { label: `Проверка: ${state}`, tone: "wait" };
  }
}

/** Общее имя из отличительного имени сертификата: `CN=Иванов И.И., O=…`. */
export function certificateName(distinguishedName: string | null): string {
  if (distinguishedName === null || distinguishedName.trim() === "") {
    return "не указано";
  }
  const common = /(?:^|,\s*)CN=((?:[^,\\]|\\.)+)/i.exec(distinguishedName);

  return common?.[1]?.trim() ?? distinguishedName;
}

/**
 * Момент времени из ответа сервера, мс.
 *
 * Подписи и запросы подписи отдают время так, как его записала база:
 * «2026-09-19 10:03:00+00» или без пояса вовсе. Такую строку Date не
 * разбирает, поэтому она приводится к ISO 8601; время без пояса — UTC.
 */
export function parseServerTime(value: string): number {
  const match =
    /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})(\.\d+)?(Z|[+-]\d{2}(?::?\d{2})?)?$/.exec(
      value,
    );
  if (match === null) return Date.parse(value);
  const [, day, time, fraction = "", zone = "Z"] = match;
  let offset = zone;
  if (zone !== "Z") {
    const digits = zone.slice(1).replace(":", "");
    offset = `${zone.slice(0, 1)}${digits.slice(0, 2)}:${digits.slice(2) || "00"}`;
  }

  return Date.parse(`${day}T${time}${fraction}${offset}`);
}

/** Дата и время для показа; неразборчивое значение выводится как есть. */
export function displayDateTime(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = parseServerTime(value);

  return Number.isNaN(parsed)
    ? value
    : new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(parsed));
}

/** Существительное в нужной форме: 1 файл, 2 файла, 5 файлов. */
export function pluralize(
  count: number,
  forms: readonly [one: string, few: string, many: string],
): string {
  const tail = count % 100;
  const last = count % 10;
  if (tail >= 11 && tail <= 14) return forms[2];
  if (last === 1) return forms[0];
  if (last >= 2 && last <= 4) return forms[1];

  return forms[2];
}

/** Форматы, которые бэкенд показывает в предпросмотре: PDF, Word и Excel. */
const PREVIEW_MIME =
  /pdf|msword|wordprocessingml|ms-excel|spreadsheetml|opendocument\.(text|spreadsheet)|^text\/csv$/i;
const PREVIEW_EXTENSION = /\.(pdf|docx?|odt|rtf|xlsx?|ods|csv)$/i;

export function canPreview(name: string, mimeType: string | null): boolean {
  return (
    PREVIEW_EXTENSION.test(name) ||
    (mimeType !== null && PREVIEW_MIME.test(mimeType))
  );
}
